import { useCallback, useMemo } from 'react';
import type { User } from '@core/generated/gql-types';
import { JobStatus } from '@core/generated/gql-types';
import { useTemplateStore } from '@core/template/useTemplateStore';
import { useJobStore } from '@core/job/useJobStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useTenantStore } from '@core/tenant/useTenantStore';
import {
  createWorkerColumns,
  type WorkerColumn,
} from '@/modules/workers/utils/workerColumns.utils';
import { getWorkerIndustryNames } from '@/modules/workers/utils/workerFilters.utils';

const getStatusBadgeClass = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'onboarding':
      return 'bg-blue-100 text-blue-800';
    case 'pending approval':
    case 'pending':
      return 'bg-orange-100 text-orange-800';
    case 'needs contact':
      return 'bg-red-100 text-red-800';
    case 'suspended':
      return 'bg-red-100 text-red-800';
    case 'rejected':
      return 'bg-gray-100 text-gray-800';
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getOnboardingPercentageStyle = (percentage: string) =>
  percentage === '100'
    ? 'bg-green-100 text-green-800'
    : 'bg-gray-100 text-gray-800';

const getWorkerTransport = (worker: User): string => {
  const hasOwnTransportAttr = worker.customAttributes?.find(
    (attr) => attr?.ref === 'hasOwnTransport',
  )?.value;
  if (hasOwnTransportAttr === undefined || hasOwnTransportAttr === null)
    return '-';
  return hasOwnTransportAttr.toLowerCase() === 'true'
    ? 'Own Transport'
    : 'Public Transport';
};

const getWorkerGender = (worker: User): string =>
  worker.customAttributes?.find((attr) => attr?.ref === 'gender')?.value || '-';

export type UseWorkerColumnsResult = {
  columns: WorkerColumn[];
  getWorkerIndustryNames: (worker: User) => string[];
  getWorkerTransport: (worker: User) => string;
  getWorkerGender: (worker: User) => string;
  getWorkerJobInfo: (workerId: string) => {
    completedCount: number;
    lastJobEndDate?: string | null;
  };
};

export const useWorkerColumns = (): UseWorkerColumnsResult => {
  const { getTerm } = useTemplateStore();
  const { getJobsByWorkerId } = useJobStore();
  const { getTenantUserById } = useTenantStore();
  const {
    classificationGroups,
    getClassificationById,
    getClassificationLevelById,
  } = useClassificationStore();

  const classificationStoreParams = useMemo(
    () => ({
      classificationGroups: classificationGroups ?? [],
      getClassificationById,
      getClassificationLevelById,
    }),
    [classificationGroups, getClassificationById, getClassificationLevelById],
  );

  const getWorkerIndustryNamesWithParams = useCallback(
    (worker: User) => getWorkerIndustryNames(worker, classificationStoreParams),
    [classificationStoreParams],
  );

  const getWorkerJobInfo = useCallback(
    (workerId: string) => {
      const jobs = getJobsByWorkerId(workerId);
      const completedJobs = jobs.filter(
        (job) => job.itemStatus === JobStatus.Filled && job.endDate,
      );
      const sortedCompletedJobs = completedJobs.sort((a, b) => {
        const aDate = parseInt(a.endDate || '0');
        const bDate = parseInt(b.endDate || '0');
        return bDate - aDate;
      });
      const lastJob = sortedCompletedJobs[0];
      return {
        completedCount: completedJobs.length,
        lastJobEndDate: lastJob?.endDate,
      };
    },
    [getJobsByWorkerId],
  );

  const columns = useMemo(
    () =>
      createWorkerColumns({
        getTerm,
        getStatusBadgeClass,
        getOnboardingPercentageStyle,
        classificationStoreParams,
        getClassificationById,
        getClassificationLevelById,
        getWorkerTransport,
        getWorkerGender,
        getWorkerJobInfo,
        getTenantUserById,
      }),
    [
      getTerm,
      classificationStoreParams,
      getClassificationById,
      getClassificationLevelById,
      getWorkerJobInfo,
      getTenantUserById,
    ],
  );

  return useMemo(
    () => ({
      columns,
      getWorkerIndustryNames: getWorkerIndustryNamesWithParams,
      getWorkerTransport,
      getWorkerGender,
      getWorkerJobInfo,
    }),
    [columns, getWorkerIndustryNamesWithParams, getWorkerJobInfo],
  );
};
